import { PartnerTypeLabel, SUB_PARTNER_ACCOUNT_STATUSES, SubPartnerAccountStatus } from 'vopay';

/**
 * The onboarding stages a partner account created from the portal passes through on its way to
 * Active, in the order earthnode moves it (`VIOPartnerAccountWorkflowStageHelper::getPartnerNextStatus`).
 * `POST /partner/create-partner` gives a new partner account its creator's own type (ID-14112), so the
 * stages follow the logged-in partner's type, as the Accounts page's do; any other populated status is
 * appended after them, so a partner account on another path is never hidden.
 *
 * Document Review sits between Agreement Pending and the next stage on every path, but it is not in
 * the `VIOPartnerAccounts.AccountStatus` enum the status summary documents, so it is not a
 * `SubPartnerAccountStatus` the list can filter on and is left out.
 *
 * `isPartnerLiability` mirrors the VAR flow's check on `PartnerLiability`: a VAR - PaaS partner account
 * carrying it skips Account Review and Validation and goes from Pre-Application Check straight to
 * Manager Approval.
 */
export function getPartnerOnboardingPipeline(
  partnerTypeLabel: PartnerTypeLabel,
  isPartnerLiability: boolean,
): SubPartnerAccountStatus[] {
  // The agreement step every path shares; Document Review (not a filterable status) follows it.
  const agreement: SubPartnerAccountStatus[] = [
    'Account Review and Validation',
    'Manager Approval',
    'Agreement Pending',
  ];

  switch (partnerTypeLabel) {
    // Technology and Referral ISO partner accounts activate straight out of Account Provisioning;
    // Referral ISVs certify first.
    case 'Technology Partner':
    case 'Referral ISO':
      return [...agreement, 'Account Provisioning'];
    case 'Referral ISV':
    case 'Referral - ISV RevShare':
      return [...agreement, 'Account Provisioning', 'Certification Pending'];
    case 'VAR - PaaS':
      return [
        'Pre-Application Check',
        ...(isPartnerLiability ? agreement.slice(1) : agreement),
        'Credit Risk Review',
        'Account Provisioning',
        'Certification Pending',
      ];
    case 'VAR - PayFac (Non-Reporting)':
      return [
        'Pre-Application Check',
        ...agreement,
        'Credit Risk Review',
        'Account Provisioning',
        'Certification Pending',
      ];
    // Reporting liability sends the partner account through the application and underwriting review.
    case 'VAR - PayFac (Reporting)':
      return [
        'Pre-Application Check',
        ...agreement,
        'Application Pending',
        'Underwriting Review',
        'Credit Risk Review',
        'Account Provisioning',
        'Certification Pending',
      ];
    // Inside Sales cannot create partner accounts; an unknown or not-yet-loaded type has no fixed
    // pipeline either. Populated statuses still show.
    default:
      return [];
  }
}

/** Statuses a partner account holds once it has left (or been pulled out of) the onboarding pipeline. */
export const PARTNER_TERMINAL_STATUSES: readonly SubPartnerAccountStatus[] = ['Active', 'Disabled', 'Rejected'];

/**
 * The statuses the summary card shows as tiles and the list's Status filter offers — one list, in
 * one order, so a tile and its option always match (the Accounts page's ID-13964 D3 rule):
 *
 * 1. the logged-in partner's pipeline, zero included, so it reads as a whole;
 * 2. every other status that holds partner accounts, plus the selected one when nothing else puts it
 *    in the list (a deep link to a stage the workflow no longer reaches), in workflow order;
 * 3. the terminal statuses, zero included.
 *
 * A status the summary reports but the portal has no name for gets no tile: the list cannot filter
 * on it (the URL guard drops it), so a tile would be a dead click. It still counts toward the
 * card's totals.
 */
export function getPartnerStatusTiles(
  partnerTypeLabel: PartnerTypeLabel,
  isPartnerLiability: boolean,
  statusCounts: { [status: string]: number | undefined },
  selectedStatus: SubPartnerAccountStatus | '',
): SubPartnerAccountStatus[] {
  const pipeline = getPartnerOnboardingPipeline(partnerTypeLabel, isPartnerLiability);

  const others = SUB_PARTNER_ACCOUNT_STATUSES.filter(
    (status) =>
      !pipeline.includes(status) &&
      !PARTNER_TERMINAL_STATUSES.includes(status) &&
      ((statusCounts[status] ?? 0) > 0 || status === selectedStatus),
  );

  return [...pipeline, ...others, ...PARTNER_TERMINAL_STATUSES];
}
